<template>
  <div
    :class="_class"
    v-on="$listeners"
  >
    <label
      :class="_labelClass"
      :style="_labelStyle"
      :for="labelFor"
      v-if="_showLabel"
    ><slot name="label">{{ label }}</slot></label>

    <div :class="_contentClass">
      <slot />
      <div :class="_errorMsgClass">
        <span>{{ validateMessage }}</span>
      </div>
    </div>
    <div
      :class="_iconClass"
      v-if="statusIcon"
    >
      <tooltip
        :message="tip"
        v-if="tip || $scopedSlots.tip"
      >
        <template v-slot:message>
          <slot name="tip" />
        </template>
        <icon icon="line_circle_help" />
      </tooltip>
      <icon
        icon="loading_gradient"
        v-if="status === 'loading'"
      />
      <icon
        icon="circle_tick"
        v-if="status === 'success'"
      />
      <icon
        icon="circle_error"
        v-if="status === 'warning'"
      />
      <icon
        icon="circle_clear"
        v-if="status === 'error'"
      />
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import AsyncValidator from 'async-validator';
import Emitter from '../scripts/mixins/emitter';
import Icon from '../icon';
import Tooltip from '../tooltip';

const emptyFn = () => {};

const name = 'ten-form-item';
AsyncValidator.warning = function() {};

function getPropByPath(obj, path) {
  let tempObj = obj;
  path = path.replace(/\[(\w+)\]/g, '.$1');
  path = path.replace(/^\./, '');

  const keyArr = path.split('.');
  let i = 0;

  for (let len = keyArr.length; i < len - 1; ++i) {
    const key = keyArr[i];
    if (key in tempObj) {
      tempObj = tempObj[key];
    } else {
      throw new Error('prop传值不正确');
    }
  }
  return {
    o: tempObj,
    k: keyArr[i],
    v: tempObj[keyArr[i]],
  };
}

export default {
  name,
  provide() {
    return {
      ['ten' + 'FormItem']: this,
    };
  },
  components: {
    Icon,
    Tooltip,
  },
  mixins: [Emitter],
  inject: ['form'],
  props: {
    /**
     * 原生 label 的文本
     */
    label: String,
    prop: String,
    required: Boolean,
    /**
     * 表单验证规则
     */
    rules: [Object, Array],
    /**
     * 表单域验证错误信息文本
     */
    error: String,
    validateStatus: Boolean,
    /**
     * 原生 label 的 for 属性
     */
    labelFor: String,
    /**
     *  原生 label 的宽度
     */
    labelWidth: String,
    /**
     * 提示
     */
    tip: [String],
    /**
     * 状态
     * @member error | loading | success | warning
     */
    status: String,
    /**
     * 是否显示状态图标
     */
    statusIcon: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      dataRules: [],
      isRequired: false,
      validateState: this.status,
      validateMessage: this.error,
      validateDisabled: false,
      validator: {},
      errorLocation: this.form.errorLocation,
    };
  },
  computed: {
    _class() {
      return [
        `${name}`,
        {
          [`${name}--required`]: this.required || this._requried,
          [`${name}--error`]: this.validateState === 'error',
          [`${name}--warning`]: this.validateState === 'warning',
          [`${name}-validating`]: this.validateState === 'validating',
          [`${name}--icon`]: this.statusIcon && (this.tip || this.status || this.$scopedSlots.tip),
        },
      ];
    },
    _requried() {
      return this.required || this.dataRules.some(rule => rule.required);
    },
    _labelClass() {
      return [`${name}-label`];
    },
    _labelStyle() {
      return {
        width: this.labelWidth || this.form.labelWidth,
      };
    },
    _contentClass() {
      return [`${name}-control`];
    },
    _errorMsgClass() {
      return [`${name}-err`];
    },
    _iconClass() {
      return [`${name}-icon`];
    },
    _showLabel() {
      return typeof this.label !== 'undefined' || this.labelWidth || this.$scopedSlots.label;
    },
    fieldValue: {
      cache: false,
      get() {
        const { model } = this.form;
        if (!model || !this.prop) {
          return;
        }

        const path = this.getPath(this.prop);
        return getPropByPath(model, path).v;
      },
    },
  },
  watch: {
    error(val) {
      this.validateMessage = val;
      this.validateState = val === '' ? '' : 'error';
    },
    validateStatus(val) {
      this.validateState = val;
    },
    rules() {
      this.updateRules();
    },
    status(val) {
      this.validateState = val;
    },
  },
  mounted() {
    if (this.prop) {
      this.dispatch('ten-form', 'form-item.add', this);

      Object.defineProperty(this, 'initialValue', {
        value: this.fieldValue,
      });
      this.$on('form.blur', this.onFieldBlur);
      this.$on('form.change', this.onFieldChange);
      this.updateRules();
    }
  },
  beforeDestroy() {
    this.dispatch('ten-form', 'form-item.remove', this);
  },
  methods: {
    getPath(prop) {
      let path = prop;
      if (path.indexOf(':') !== -1) {
        path = path.replace(/:/, '.');
      }
      return path;
    },
    /**
     * 重置表单项
     * @public
     */
    resetField() {
      this.clearValidate();

      const defaultModel = this.form.formDefaultModel;
      const { model } = this.form;
      let path = this.prop;
      if (path.indexOf(':') !== -1) {
        path = path.replace(/:/, '.');
      }

      const defaultProp = getPropByPath(defaultModel, path, true);
      const prop = getPropByPath(model, path, true);

      prop.o[prop.k] = cloneDeep(defaultProp.o[prop.k]);
    },
    /**
     * 清除表单项验证错误
     * @public
     */
    clearValidate() {
      this.validateState = '';
      this.validateMessage = '';
      this.validateDisabled = true;
      setTimeout(() => {
        this.validateDisabled = false;
      }, 300);
    },
    updateRules() {
      let formRules = this.form.rules;
      const selfRules = this.rules;

      formRules = formRules ? formRules[this.prop] : [];

      this.dataRules = [].concat(selfRules || formRules || []);
    },
    getFilteredRule(trigger) {
      const rules = this.dataRules;

      if (!trigger) {
        return rules;
      }

      return rules.filter((rule) => {
        let ruleTriggers = rule.trigger;
        if (!ruleTriggers) {
          return true;
        }
        if (!Array.isArray(ruleTriggers)) {
          ruleTriggers = [ruleTriggers];
        }
        return ruleTriggers.indexOf(trigger) > -1;
      });
    },
    /**
     * 验证表单项
     */
    validate(trigger, callback = () => {}) {
      const rules = this.getFilteredRule(trigger);
      if (!rules || rules.length === 0) {
        callback();
        return Promise.resolve('');
      }

      this.validateDisabled = false;
      this.validateState = 'validating';

      const descriptor = {};
      descriptor[this.prop] = rules;

      const validator = new AsyncValidator(descriptor);
      const model = {};

      model[this.prop] = this.fieldValue;

      const promise = new Promise((resolve, reject) => {
        validator.validate(model, { firstFields: true }, (errors) => {
          this.validateState = !errors ? 'success' : 'error';
          this.validateMessage = errors ? errors[0].message : '';

          callback(errors);
          errors ? reject(errors) : resolve('');
        });
      });

      promise.catch(emptyFn);
      return promise;
    },
    onFieldBlur() {
      this.validate('blur');
    },
    onFieldChange() {
      if (this.validateDisabled) {
        return;
      }

      this.validate('change');
    },
  },
};
</script>
